import React, { useCallback, useEffect, useRef, type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	CloseButton,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AvatarGroup from '@atlaskit/avatar-group';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../controller/Controller';
import type { RestrictedUserPrincipal } from '../commonTypes';

import { LearnMoreChunk } from './LearnMoreChunk';

export type RestrictedAccessWarningDialogProps = {
	restrictedUserPrincipals: RestrictedUserPrincipal[] | undefined;
	pageTitle: string | null | undefined;
	triggeredBy: 'accessRequests' | 'share';
	onSkip: () => void;
	onCloseComplete: () => void;
};

const i18n = defineMessages({
	headingSingleUser: {
		id: 'share-and-restrict-dialog.restricted-access-warning.heading.single-user',
		defaultMessage: 'Someone needs additional access',
		description: 'Heading for a pop-up modal that informs that a user need aditional access',
	},
	headingMultiUser: {
		id: 'share-and-restrict-dialog.restricted-access-warning.heading.multi-user',
		defaultMessage: 'Some people need additional access',
		description: 'Heading for a pop-up modal that informs that some users need aditional access',
	},
	body: {
		id: 'share-and-restrict-dialog.restricted-access-warning.body',
		defaultMessage:
			'They need access elsewhere first before they can have the indicated access to <b>{page}</b>. Contact a space admin or <learnMoreLink>read our troubleshooting guide</learnMoreLink>. ',
		description: 'Body for a pop-up modal that informs that some users need aditional access',
	},
	usersNameList: {
		id: 'share-and-restrict-dialog.restricted-access-warning.users',
		defaultMessage:
			'{usersCount, plural, one {{firstUser}} =2 {{firstUser} and {secondUser}} =3 {{firstUser}, {secondUser}, and {thirdUser}} =4 {{firstUser}, {secondUser}, {thirdUser} and {fourthUser}} other {{firstUser}, {secondUser}, {thirdUser} and {remainingUserCount} others}}',
		description: 'Text to show a list of users that have restricted access',
	},
	skipButton: {
		id: 'share-and-restrict-dialog.restricted-access-warning.skip',
		defaultMessage: 'OK',
		description: 'The label for a button that will skip any further action',
	},
});

export const RestrictedAccessWarningDialog: FC<RestrictedAccessWarningDialogProps> = ({
	restrictedUserPrincipals,
	pageTitle,
	triggeredBy,
	onSkip,
	onCloseComplete,
}) => {
	const focusRef = useRef<HTMLButtonElement>(null);
	const usersCount = restrictedUserPrincipals ? restrictedUserPrincipals.length : 0;
	const users = restrictedUserPrincipals
		? restrictedUserPrincipals.map((restrictedPrincipal) => restrictedPrincipal.userPrincipal)
		: undefined;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { Modal } = useChoreographerAssets();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'RestrictedAccessWarningDialog',
				attributes: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					blockedUsersCount: usersCount,
					triggeredBy,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, usersCount, restrictedUserPrincipals, triggeredBy]);

	const handleSkip = useCallback(() => {
		onSkip();
	}, [onSkip]);

	const avatarGroupData = users
		? users.map((user) => ({
				key: user?.email || '',
				name: user?.name || '',
				href: '#',
				src: user?.avatarUrl || '',
			}))
		: [];

	return (
		<Modal
			width="medium"
			autoFocus={focusRef}
			onCloseComplete={onCloseComplete}
			testId="modal-dialog-restricted-access-warning"
		>
			<ModalHeader>
				<ModalTitle>
					<FormattedMessage
						{...(usersCount == 1 ? i18n.headingSingleUser : i18n.headingMultiUser)}
					/>
				</ModalTitle>
				<CloseButton onClick={handleSkip} />
			</ModalHeader>
			<ModalBody>
				<Stack space="space.200">
					<Inline space="space.100" alignBlock="center">
						<AvatarGroup appearance="stack" data={avatarGroupData} maxCount={4} />
						<Text weight="bold">
							<FormattedMessage
								{...i18n.usersNameList}
								values={{
									usersCount,
									firstUser: users && users[0] ? users[0].name : null,
									secondUser: users && users[1] ? users[1].name : null,
									thirdUser: users && users[2] ? users[2].name : null,
									fourthUser: users && users[3] ? users[3].name : null,
									remainingUserCount: usersCount - 3,
								}}
							/>
						</Text>
					</Inline>
					<FormattedMessage
						{...i18n.body}
						values={{
							page: pageTitle,
							b: (chunk) => <Text weight="bold">{chunk}</Text>,
							learnMoreLink: LearnMoreChunk,
						}}
					/>
				</Stack>
			</ModalBody>
			<ModalFooter>
				<Button appearance="primary" onClick={handleSkip} ref={focusRef}>
					<FormattedMessage {...i18n.skipButton} />
				</Button>
			</ModalFooter>
		</Modal>
	);
};
